import { type JSONContent, Node } from "@tiptap/core";
import { NodeViewContent, NodeViewWrapper, type ReactNodeViewProps, ReactNodeViewRenderer } from "@tiptap/react";
import { isValidTemplateSlotParamName } from "./editor-extension-template-slot";

export const TEMPLATE_CONDITIONAL_NODE_NAME = "templateConditional";
export const TEMPLATE_CONDITIONAL_CLASS_NAME = "editor-extension-template-conditional";

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const normalizeTemplateConditionalParam = (value: unknown): string => String(value ?? "").trim();

const TemplateConditionalNodeComponent = (props: ReactNodeViewProps<HTMLDivElement>) => {
  const param = normalizeTemplateConditionalParam(props.node.attrs.param);

  return (
    <NodeViewWrapper className={`${TEMPLATE_CONDITIONAL_CLASS_NAME} my-3 block max-w-full`}>
      <div className="rounded-md border border-primary/25 bg-primary/5 in-[.ProseMirror-selectednode]:ring-2 in-[.ProseMirror-selectednode]:ring-primary/50">
        <div
          contentEditable={false}
          className="flex select-none items-center gap-1 border-b border-primary/20 px-2 py-1 text-xs font-medium text-primary"
        >
          {/* "if <param>" read as code to the brokers authoring these. Say what the
              block actually does, keeping the field name in mono so it still reads
              as the declared field it refers to. */}
          <span>Only included when</span>
          <span className="font-mono">{param}</span>
        </div>
        <NodeViewContent className="px-3 py-2" />
      </div>
    </NodeViewWrapper>
  );
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    TemplateConditional: {
      setTemplateConditional: (param?: string) => ReturnType;
      unsetTemplateConditional: () => ReturnType;
    };
  }
}

export const EditorExtensionTemplateConditional = Node.create({
  name: TEMPLATE_CONDITIONAL_NODE_NAME,
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      param: {
        default: "",
        rendered: false,
      },
    };
  },

  addCommands() {
    return {
      setTemplateConditional:
        (rawParam?: string) =>
        ({ commands }) => {
          const param = normalizeTemplateConditionalParam(rawParam);
          if (!isValidTemplateSlotParamName(param)) {
            return false;
          }

          return commands.wrapIn(this.name, { param });
        },
      unsetTemplateConditional:
        () =>
        ({ commands }) =>
          commands.lift(this.name),
    };
  },

  parseHTML() {
    return [
      {
        priority: 100,
        // The backend renderer accepts `data-tpl-if` on any element (stored
        // templates may use `<p data-tpl-if>` / `<section data-tpl-if>`, not just
        // `<div>`), so match the attribute on any tag. `getAttrs` still rejects
        // anything without a valid param name, and `renderHTML` normalizes the
        // wrapper back to `<div data-tpl-if>` on save.
        tag: "[data-tpl-if]",
        getAttrs(node) {
          if (typeof node === "string") {
            return false;
          }

          const param = normalizeTemplateConditionalParam(node.getAttribute("data-tpl-if"));
          if (!isValidTemplateSlotParamName(param)) {
            return false;
          }

          return { param };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const param = normalizeTemplateConditionalParam(node.attrs.param);

    return ["div", { "data-tpl-if": param }, 0];
  },

  renderMarkdown({ attrs, content }: JSONContent, helpers) {
    const param = normalizeTemplateConditionalParam(attrs?.param);
    const renderedContent = helpers.renderChildren(content ?? [], "\n\n");

    if (!isValidTemplateSlotParamName(param)) {
      return renderedContent;
    }

    return `<div data-tpl-if="${escapeHtml(param)}">\n${renderedContent}\n</div>`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateConditionalNodeComponent);
  },
});
